import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  IconButton,
  Typography,
  Slider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@material-ui/core';
import TimeCode from 'commons/utils/TimeCode';

import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckIcon from '@material-ui/icons/Check';
import SaveIcon from '@material-ui/icons/Save';

import { RootState } from 'redux/reducers/type';
import { calculateWidthToPercent, eventPropagationPrevent } from 'commons/utils/commonUtil';
import { Auth, AUTHORITIES, VideoPlayMode } from 'commons/constants/KeyFlowConstants';
import { VideoPlayModeType } from 'commons/constants/type';
import { ScriptPutParamVOProps } from 'commons/vo/param/ScriptParamVO';

import { VideoStartPointWebIcon, VideoFinishPointWebIcon, VideoPlayIcon } from 'asset/images';

import { useAsset, usePreference, useVideo, useError, useScript } from 'hooks';
import { useStyles } from 'components/VideoPlayer/useStyles';
import { HlsError, Loading } from 'components/VideoPlayer';
import { HlsErrorType, VideoPlayerProps } from 'components/VideoPlayer/type';
import { SectionPause, SectionPlay } from 'components/Icon';
import Skim from './Skim';

export type KeyEventType = {
  shiftKey: boolean;
  movementX: number;
};

export type VideoType = {
  // videoTimeupdate: (currentTime: number) => void;
  getVideoInFrames: () => number;
  getVideoOutFrames: () => number;
  getInTimecode: () => {
    inHourTime: string;
    inMinuteTime: string;
    inSecondTime: string;
  };
  changeVideoInTime: (inTime: number) => void;
  changeVideoOutTime: (outTime: number) => void;
  changeVideoCurrentTime: (currentTime: number) => void;
  clearPartial: () => void;
};

/**
 *  Video Playback Rate Controller
 */
interface PlaybackRateControllerProps {
  visible: boolean;
  current: number;
  onChange: any;
}

const playbackRateList = [
  {
    value: 2,
    name: '2',
  },
  {
    value: 1.5,
    name: '1.5',
  },
  {
    value: 1,
    name: '1',
  },
  {
    value: 0.75,
    name: '0.75',
  },
  {
    value: 0.5,
    name: '0.5',
  },
];
type PlaybackRateItemProps = {
  item: { value: number; name: string };
  current: number;
  onChange: (value: number) => {};
};

const PlaybackRateItem = ({ item, current, onChange }: PlaybackRateItemProps) => {
  const onClick = useCallback(
    event => {
      eventPropagationPrevent(event);
      onChange(item.value);
    },
    [onChange, item.value],
  );

  return (
    <ListItem button key={item.value} onClick={onClick}>
      {item.value === current && (
        <ListItemIcon>
          <CheckIcon color="primary" />
        </ListItemIcon>
      )}
      <ListItemText {...(item.value !== current && { inset: true })} primary={item.name} />
    </ListItem>
  );
};

const PlaybackRateController = ({ visible, current, onChange }: PlaybackRateControllerProps) => {
  const classes = useStyles();

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      className={clsx(
        classes.playbackRateControllerContainer,
        visible || classes.playbackRateControlHidden,
      )}
    >
      <Grid item xs={12}>
        <List component="nav" aria-label="contacts">
          {playbackRateList.map((item: any) => (
            <PlaybackRateItem key={item.name} item={item} current={current} onChange={onChange} />
          ))}
        </List>
      </Grid>
    </Grid>
  );
};

export default forwardRef(
  ({ initInTime, initOutTime, onChangeScrollHeight }: VideoPlayerProps, ref: Ref<VideoType>) => {
    const [showMessageToast, setShowMessageToast] = useState<boolean>(false);

    const classes = useStyles();

    const authorities = localStorage.getItem(Auth.AUTHORITIES) || '';
    const hasAuthCleanPartial = authorities.includes(AUTHORITIES.AUTH_CLEAN_PARTIAL);
    const hasAuthEditScript = authorities.includes(AUTHORITIES.AUTH_EDIT_SCRIPT);

    const {
      duration: videoDuration,
      inTime,
      outTime,
    } = useSelector((state: RootState) => state.video);
    const { selectedAsset } = useSelector((state: RootState) => state.asset);

    const { downloadingGroup } = useSelector((state: RootState) => state.download);
    const { selectedScript, message, putError, isLoadingPut } = useSelector(
      (state: RootState) => state.script,
    );

    const { onRequestAssetDownloadCloudfront } = useAsset();
    const { videoPartialMaxTime, videoPartialMinTime } = usePreference();
    const { onRequestPutScript } = useScript();

    const {
      hls,
      bufferSecond,
      onInitVideo,
      onChangeVideoDuration,
      onChangeVideoInTime,
      onChangeVideoOutTime,
    } = useVideo();

    const videoPlayerRef = useRef<HTMLVideoElement>(null);

    const [videoError, setVideoError] = useState<HlsErrorType & { count: number }>({
      count: 0,
      type: '',
      details: '',
      fatal: false,
      url: '',
    });
    const [lowresHandled, setLowresHandled] = useState<boolean>(false);
    const [proxyHandled, setProxyHandled] = useState<boolean>(false);

    const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
    const [videoInTime, setVideoInTime] = useState<number>(0);
    const [videoOutTime, setVideoOutTime] = useState<number>(0);
    const [isInTime, setIsInTime] = useState<boolean>(false);

    // script edit controller
    const [isEditScript, setIsEditScript] = useState<boolean>(false);
    const [visibleScriptEditController, setVisibleScriptEditController] = useState<boolean>(false);

    const [menuButton, setMenuButton] = React.useState<null | HTMLButtonElement>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isPlay, setIsPlay] = useState<boolean>(true);
    const [isSkimShow, setIsSkimShow] = useState<boolean>(false);
    const [playMode, setPlayMode] = useState<VideoPlayModeType>(VideoPlayMode.NORMAL_PLAY);
    const [prevCurrentTime, setPrevCurrentTime] = useState(0);

    const [scrubbingPointX, setScrubbingPointX] = useState<number>(0);
    const [scrubbingPercent, setScrubbingPercent] = useState<number>(0);
    const [skimPercentY, setSkimPercentY] = useState<number>(98);

    const [currentPointX, setCurrentPointX] = useState<number>(0);
    const [inPointX, setInPointX] = useState(0);
    const [outPointX, setOutPointX] = useState(0);

    const [draggableProgress, setDraggableProgress] = useState<boolean>(false);
    const [draggableInPoint, setDraggableInPoint] = useState<boolean>(false);
    const [draggableOutPoint, setDraggableOutPoint] = useState<boolean>(false);

    // video volume
    const [soundValue, setSoundValue] = useState<number>(50);
    const [enableSoundControl, setEnableSoundControl] = useState<boolean>(false);

    // video playback rate
    const [visiblePlaybackRateControl, setVisiblePlaybackRateControl] = useState<boolean>(false);
    const [playbackRate, setPlaybackRate] = useState<number>(1.0);

    // PGM - CLEAN 선택
    const skipOutTimeUpdateRef = useRef(false);
    const [isClean, setIsClean] = useState<boolean>(false);

    const { isDownloading, percent } = useMemo(() => {
      const group = (selectedAsset && downloadingGroup[selectedAsset.id]) || {
        isDownloading: false,
        percent: 0,
      };

      return {
        isDownloading: group.isDownloading,
        percent: group.percent,
      };
    }, [selectedAsset, downloadingGroup]);

    const fps = useMemo(() => {
      let mediaFps = 29.97;
      if (selectedAsset) {
        mediaFps = selectedAsset.mediaFps;
      }
      return mediaFps;
    }, [selectedAsset]);

    const {
      currentTimecodeUtil,
      durationTimecodeUtil,
      inTimecodeUtil,
      outTimecodeUtil,
      scrubbingTimecodeUtil,
    } = useMemo(() => {
      const currentTimecodeUtil = new TimeCode(fps);
      const durationTimecodeUtil = new TimeCode(fps);
      const inTimecodeUtil = new TimeCode(fps);
      const outTimecodeUtil = new TimeCode(fps);
      const scrubbingTimecodeUtil = new TimeCode(fps);

      return {
        currentTimecodeUtil,
        durationTimecodeUtil,
        inTimecodeUtil,
        outTimecodeUtil,
        scrubbingTimecodeUtil,
      };
    }, [fps]);

    const currentTimecode = useMemo(() => {
      currentTimecodeUtil.setSeconds(videoCurrentTime);
      return currentTimecodeUtil.getTimeCode();
    }, [currentTimecodeUtil, videoCurrentTime]);

    const durationTimecode = useMemo(() => {
      durationTimecodeUtil.setSeconds(videoDuration);
      return durationTimecodeUtil.getTimeCode();
    }, [durationTimecodeUtil, videoDuration]);

    const currentPointLeftPercent = useMemo(
      () => `${(videoCurrentTime / videoDuration) * 100}%`,
      [videoCurrentTime, videoDuration],
    );

    const skimPixelX = useMemo(() => {
      if (videoPlayerRef.current) {
        const width = videoPlayerRef.current.offsetWidth;
        let x = scrubbingPointX;
        if (x < 120) x = 0;
        if (x >= 120 && x < width - 120) x -= 120;
        if (x >= width - 120) x = width - 240;

        return x;
      }
      return 0;
    }, [scrubbingPointX]);

    // const skimPixelY = useMemo(() => {
    //   return `0 -${skimPercentY * 67.5}px`;
    // }, [skimPercentY]);

    // const inPointLeftPercent = useMemo(() => {
    //   if (videoPlayerRef.current) {
    //     const max = outPointX - VideoInOutPointIconWidth * 2;
    //     const result = Math.min(max, inPointX);
    //     return `${(result / videoPlayerRef.current.offsetWidth) * 100}%`;
    //     // return `${(inPointX / videoPlayerRef.current.offsetWidth) * 100}%`;
    //   }
    //   return '0%';
    // }, [inPointX]);

    // const outPointRightPercent = useMemo(() => {
    //   if (videoPlayerRef.current) {
    //     const min = inPointX + VideoInOutPointIconWidth * 2;
    //     const result = Math.max(min, outPointX);
    //     return `${100 - (result / videoPlayerRef.current.offsetWidth) * 100}%`;
    //     // return `${100 - (outPointX / videoPlayerRef.current.offsetWidth) * 100}%`;
    //   }
    //   return '100%';
    // }, [outPointX]);

    const inPointLeftPercent = useMemo(() => {
      if (videoInTime && videoDuration) {
        return `${(videoInTime / videoDuration) * 100}%`;
      }
      return '0%';
    }, [videoInTime]);

    const outPointRightPercent = useMemo(() => {
      if (videoOutTime && videoDuration) {
        return `${100 - (videoOutTime / videoDuration) * 100}%`;
      }
      return '100%';
    }, [videoOutTime]);

    const scrubbingTimecode = useMemo(() => {
      const scrubbingTime = (scrubbingPercent * videoDuration) / 100;
      scrubbingTimecodeUtil.setSeconds(Math.round(scrubbingTime));
      return scrubbingTimecodeUtil.getTimeCode();
    }, [scrubbingPercent]);

    const bufferPercent = useMemo(() => {
      return `${(bufferSecond / videoDuration) * 100}%`;
    }, [bufferSecond]);

    const { skimPath, thumbnailPath, lowresPath, streamingUrl, proxyPath } = useMemo(() => {
      return {
        skimPath: selectedAsset?.skimPath || '',
        thumbnailPath: selectedAsset?.filePath.thumbnail.cloudfront || '',
        lowresPath: selectedAsset?.filePath.lowres.cloudfront || '',
        streamingUrl: selectedAsset?.streamingUrl || '',
        ...(selectedAsset?.filePath.proxy && {
          proxyPath: selectedAsset?.filePath.proxy.cloudfront || '',
        }),
      };
    }, [selectedAsset]);

    const isShow = useMemo(() => {
      return !!selectedAsset?.filePath.proxy && hasAuthCleanPartial;
    }, [selectedAsset]);

    useImperativeHandle(
      ref,
      () => ({
        getVideoInFrames: () => {
          inTimecodeUtil.setSeconds(videoInTime);
          return inTimecodeUtil.getFrames();
        },
        getVideoOutFrames: () => {
          outTimecodeUtil.setSeconds(videoOutTime);
          return outTimecodeUtil.getFrames();
        },
        getInTimecode: () => {
          console.debug(videoInTime);
          inTimecodeUtil.setSeconds(videoInTime);
          return {
            inHourTime: String(inTimecodeUtil.getValueOfHours()),
            inMinuteTime: String(inTimecodeUtil.getValueOfMinutes()),
            inSecondTime: String(inTimecodeUtil.getValueOfSeconds()),
          };
        },
        changeVideoInTime: (inTime: number) => {
          if (videoPlayerRef.current) {
            const percent = inTime / videoDuration;
            const x = videoPlayerRef.current.offsetWidth * percent;
            setVideoInTime(inTime);
            onChangeVideoInTime(inTime);
            setInPointX(x);
          }
        },
        changeVideoOutTime: (outTime: number) => {
          if (videoPlayerRef.current) {
            const percent = outTime / videoDuration;
            const x = videoPlayerRef.current.offsetWidth * percent;
            setVideoOutTime(outTime);
            onChangeVideoOutTime(outTime);
            setOutPointX(x);
          }
        },
        changeVideoCurrentTime: (currentTime: number) => {
          if (videoPlayerRef.current) {
            const percent = currentTime / videoDuration;
            const x = videoPlayerRef.current.offsetWidth * percent;
            setVideoCurrentTime(currentTime);
            setCurrentPointX(x);
            videoPlayerRef.current.currentTime = currentTime;
          }
        },
        clearPartial: () => {
          if (videoPlayerRef.current) {
            setVideoInTime(0);
            onChangeVideoInTime(0);
            setInPointX(0);

            setVideoOutTime(videoDuration);
            onChangeVideoOutTime(videoDuration);
            setOutPointX(videoPlayerRef.current.offsetWidth);
          }
        },
      }),
      [
        videoPlayerRef.current,
        videoInTime,
        videoOutTime,
        inTimecodeUtil,
        outTimecodeUtil,
        onChangeVideoInTime,
        onChangeVideoOutTime,
      ],
    );

    useLayoutEffect(() => {
      const videoPlayer = videoPlayerRef.current;
      // [테스트 코드]
      // streamingUrl = `http://wkbl.kbsn.co.kr:1935/vod/_definst_/mp4:200730000012/V201122000002.mp4/playlist.m3u8`;
      console.debug(`lowresPath: ${lowresPath}`);

      if (videoPlayer && streamingUrl) {
        videoPlayer.load();
        onInitVideo(videoPlayer, streamingUrl, fps);
      }
    }, [onInitVideo, fps, lowresPath, streamingUrl]);

    // useEffect(() => {
    //   hls.on(Hls.Events.ERROR, function (event, error: Hls.errorData) {
    //     const type = error.type;
    //     const details = error.details;
    //     const fatal = error.fatal;
    //     const url = error.url || '';

    //     if (fatal) {
    //       setVideoError({
    //         type,
    //         details,
    //         fatal,
    //         url,
    //       });
    //       setIsLoading(false);
    //       switch (type) {
    //         case Hls.ErrorTypes.NETWORK_ERROR:
    //           // try to recover network error
    //           console.debug('fatal network error encountered, try to recover');
    //           hls.startLoad();
    //           break;
    //         case Hls.ErrorTypes.MEDIA_ERROR:
    //           console.debug('fatal media error encountered, try to recover');
    //           hls.recoverMediaError();
    //           break;
    //         default:
    //           // cannot recover
    //           hls.destroy();
    //           break;
    //       }
    //     }
    //   });
    // }, [hls]);

    useLayoutEffect(() => {
      function onPressSpacebar() {
        const videoPlayer = videoPlayerRef.current;
        if (videoPlayer) {
          if (isPlay) {
            videoPlayer.pause();
            setIsPlay(false);
          } else {
            /**
             * TODO:
             *  비디오가 플레이 준비가 안되면 에러를 반환 함, 그래서 로딩 로직 추가
             *  다른 play(), pause() 로직 모두 추가해야 함.
             */
            const playPromise = videoPlayer.play();
            if (playPromise) {
              playPromise
                .then(() => {
                  setIsPlay(true);
                })
                .catch(error => {
                  console.error(error);
                  setIsLoading(true);
                });
            }
          }
        }
      }

      function onPressLeftKey(controlValue: number) {
        if (videoPlayerRef.current) {
          let videoCurrentTime = 0;
          if (videoPlayerRef.current.currentTime - controlValue <= 0) {
            videoCurrentTime = 0;
          } else {
            videoCurrentTime = videoPlayerRef.current.currentTime - controlValue;
          }
          setVideoCurrentTime(videoCurrentTime);
          videoPlayerRef.current.currentTime = videoCurrentTime;
        }
      }

      function onPressRightKey(controlValue: number) {
        if (videoPlayerRef.current) {
          let videoCurrentTime = 0;
          if (
            videoPlayerRef.current.currentTime + controlValue >=
            videoPlayerRef.current.duration
          ) {
            videoCurrentTime = videoPlayerRef.current.duration;
          } else {
            videoCurrentTime = videoPlayerRef.current.currentTime + controlValue;
          }
          setVideoCurrentTime(videoCurrentTime);
          videoPlayerRef.current.currentTime = videoCurrentTime;
        }
      }

      // 현재 시점 기준으로 -10/+10초 구간으로 인/아웃 포인트 설정
      function adjustInOutPoint() {
        setIsEditScript(true);

        if (!videoPlayerRef.current) return;

        const { duration, offsetWidth, currentTime } = videoPlayerRef.current;

        const delta = 10; // seconds

        let newIn = Math.max(0, currentTime - delta);
        let newOut = Math.min(duration, currentTime + delta);

        let newInX = (newIn / duration) * offsetWidth;
        let newOutX = (newOut / duration) * offsetWidth;

        onChangeVideoInTime(newIn);
        setVideoInTime(newIn);
        setInPointX(newInX);

        onChangeVideoOutTime(newOut);
        setVideoOutTime(newOut);
        setOutPointX(newOutX);
      }

      function onPressIKey() {
        setIsEditScript(true);
        const minTime = Number(videoPartialMinTime);
        if (videoPlayerRef.current) {
          const { duration, offsetWidth, currentTime } = videoPlayerRef.current;

          let _inTime = currentTime;
          let _outTime = outTime;
          let _inPointX = (currentTime / duration) * offsetWidth;
          let _outPointX = outPointX;
          if (
            // [1] Out Time 보다 In Time 을 크게 잡을 때
            currentTime > outTime - minTime &&
            // [1] 최소 시간 보다 작을 때
            currentTime < duration - minTime
          ) {
            const outPointPercent = (currentTime + minTime) / duration;
            _outPointX = offsetWidth * outPointPercent;
            _outTime = currentTime + minTime;
          } else if (
            // [2] Out Time 보다 In Time 을 크게 잡을 때
            currentTime > outTime - minTime &&
            // [2] 최소 시간 보다 클 때
            currentTime >= duration - minTime
          ) {
            const inPointPercent = (duration - minTime) / duration;
            _inTime = duration - minTime;
            _inPointX = inPointPercent * offsetWidth;
            _outTime = duration;
            _outPointX = offsetWidth;
          }
          onChangeVideoInTime(_inTime);
          setVideoInTime(_inTime);
          setInPointX(_inPointX);

          onChangeVideoOutTime(_outTime);
          setVideoOutTime(_outTime);
          setOutPointX(_outPointX);
        }
      }

      function onPressOKey() {
        setIsEditScript(true);
        const minTime = Number(videoPartialMinTime);

        if (videoPlayerRef.current) {
          const { duration, offsetWidth, currentTime } = videoPlayerRef.current;
          let _inTime = inTime;
          let _inPointX = inPointX;
          let _outTime = currentTime;
          let _outPointX = (currentTime / duration) * offsetWidth;

          if (
            // [1] In Time 보다 Out Time 이 작을 때
            currentTime < inTime + minTime &&
            // [1] 최소 시간 보다 클 때
            currentTime > minTime
          ) {
            const inPointPercent = (currentTime - minTime) / duration;
            _inPointX = offsetWidth * inPointPercent;
            _inTime = currentTime - minTime;
          } else if (
            // [1] In Time 보다 Out Time 이 작을 때
            currentTime < inTime + minTime &&
            // [1] 최소 시간 보다 작을 때
            currentTime <= minTime
          ) {
            const outPointPercent = (currentTime - minTime) / duration;
            _inTime = 0;
            _inPointX = 0;
            _outTime = currentTime - minTime;
            _outPointX = outPointPercent * offsetWidth;
          }
          onChangeVideoInTime(_inTime);
          setVideoInTime(_inTime);
          setInPointX(_inPointX);

          onChangeVideoOutTime(_outTime);
          setVideoOutTime(_outTime);
          setOutPointX(_outPointX);
        }
      }

      const keyDown = (event: KeyboardEvent) => {
        // input 필드에 포커스가 있으면 키 이벤트를 처리하지 않음
        const activeElement = document.activeElement;

        if (activeElement && activeElement.tagName === 'INPUT') return;

        const controlValue = event.altKey ? 1 : 5;
        event.preventDefault();

        if (videoPlayerRef.current) {
          switch (event.keyCode) {
            // [스페이스 바]
            case 32: {
              onPressSpacebar();
              break;
            }
            // [왼쪽 방향키]
            case 37: {
              onPressLeftKey(controlValue);
              break;
            }
            // [오른쪽 방향키]
            case 39: {
              onPressRightKey(controlValue);
              break;
            }
            // [',' 1 frame 뒤로]
            case 188: {
              const frame = 1 / fps;
              onPressLeftKey(frame);
              break;
            }
            // ['.' 1 frame 앞으로]
            case 190: {
              const frame = 1 / fps;
              onPressRightKey(frame);
              break;
            }

            // [ctrl / command + u] InPoint -10s / OutPoint +10s
            case 85: {
              if (event.ctrlKey || event.metaKey) {
                adjustInOutPoint();
              }
              break;
            }

            // ['i'] 현재 시간 In Time 설정
            case 73: {
              if (event.ctrlKey || event.metaKey) {
                onPressIKey();
              }
              break;
            }
            // ['o'] 현재 시간 Out Time 설정
            case 79: {
              if (event.ctrlKey || event.metaKey) {
                onPressOKey();
              }
              break;
            }
            // 240324000001 test
            // ['tab'] inTime <-> outTime toggle
            case 9: {
              if (isInTime) {
                if (videoPlayerRef.current) {
                  videoPlayerRef.current.currentTime = inTime;
                  setIsInTime(false);
                }
              } else {
                if (videoPlayerRef.current) {
                  videoPlayerRef.current.currentTime = outTime;
                  setIsInTime(true);
                }
              }
              break;
            }
            // Ctrl+S / Cmd+S 저장
            case 83: {
              if (event.ctrlKey || event.metaKey) {
                if (selectedAsset) {
                  handleRequestPutScript();
                }
              }
              break;
            }
            default:
              break;
          }
        }
      };
      document.addEventListener('keydown', keyDown, { capture: false });
      return () => {
        document.removeEventListener('keydown', keyDown);
      };
    }, [isPlay, inTime, inPointX, outTime, outPointX, videoPartialMinTime, fps, isInTime]);

    useLayoutEffect(() => {
      const mouseUp = () => {
        if (videoPlayerRef.current && draggableProgress) {
          videoPlayerRef.current.play();
          setDraggableProgress(false);
          setIsPlay(true);
          setIsSkimShow(false);
        }
      };
      document.addEventListener('mouseup', mouseUp, false);

      return () => {
        document.removeEventListener('mouseup', mouseUp);
      };
    }, [draggableProgress, draggableInPoint, draggableOutPoint, videoInTime, videoOutTime]);

    useLayoutEffect(() => {
      const mouseUpByInPoint = () => {
        if (videoPlayerRef.current && draggableInPoint) {
          setDraggableInPoint(false);
          onChangeVideoInTime(videoInTime);
          setIsSkimShow(false);
          setIsPlay(true);
          videoPlayerRef.current.play();
        }
      };
      document.addEventListener('mouseup', mouseUpByInPoint, false);

      return () => {
        document.removeEventListener('mouseup', mouseUpByInPoint);
      };
    }, [videoPlayerRef.current, videoInTime, draggableInPoint]);

    useLayoutEffect(() => {
      const mouseUpByOutPoint = () => {
        if (videoPlayerRef.current && draggableOutPoint) {
          setDraggableOutPoint(false);
          onChangeVideoOutTime(videoOutTime);
          setIsSkimShow(false);
          setIsPlay(true);
          videoPlayerRef.current.play();
        }
      };
      document.addEventListener('mouseup', mouseUpByOutPoint, false);

      return () => {
        document.removeEventListener('mouseup', mouseUpByOutPoint);
      };
    }, [videoPlayerRef.current, videoOutTime, draggableOutPoint, soundValue]);

    useLayoutEffect(() => {
      const mouseMoveProgress = ({ shiftKey, movementX }: KeyEventType) => {
        if (draggableProgress && videoPlayerRef.current) {
          const x = shiftKey ? currentPointX + movementX * 0.1 : currentPointX + movementX;
          const width = videoPlayerRef.current.offsetWidth;
          const percent: number = calculateWidthToPercent(width, x);
          const videoCurrentTime = percent * videoPlayerRef.current.duration;

          setCurrentPointX(x);
          setScrubbingPointX(x);
          setScrubbingPercent(percent * 100);
          setVideoCurrentTime(videoCurrentTime);
          videoPlayerRef.current.currentTime = videoCurrentTime;
        }
      };
      window.addEventListener('mousemove', mouseMoveProgress, false);

      return () => {
        window.removeEventListener('mousemove', mouseMoveProgress);
      };
    }, [draggableProgress, currentPointX, videoPlayerRef.current]);

    useLayoutEffect(() => {
      const mouseMoveIn = ({ shiftKey, movementX }: KeyEventType) => {
        if (draggableInPoint && videoPlayerRef.current) {
          const x = shiftKey ? inPointX + movementX * 0.1 : inPointX + movementX;
          const width = videoPlayerRef.current.offsetWidth;
          const percent = calculateWidthToPercent(width, x);
          const time = videoDuration * percent;
          const minInTime: number = Number(videoOutTime) - Number(videoPartialMinTime);

          if (time <= minInTime) {
            setInPointX(x);
            setScrubbingPointX(x);
            setCurrentPointX(x);
            setScrubbingPercent(percent * 100);
            setVideoInTime(time);
            setVideoCurrentTime(time);
            videoPlayerRef.current.currentTime = time;
          }
        }
      };
      window.addEventListener('mousemove', mouseMoveIn, false);

      return () => {
        window.removeEventListener('mousemove', mouseMoveIn);
      };
    }, [draggableInPoint, videoDuration, inPointX, videoOutTime, videoPartialMinTime]);

    useLayoutEffect(() => {
      const mouseMoveOut = ({ shiftKey, movementX }: KeyEventType) => {
        if (draggableOutPoint && videoPlayerRef.current) {
          const x = shiftKey ? outPointX + movementX * 0.1 : outPointX + movementX;
          const width = videoPlayerRef.current.offsetWidth;
          const percent = calculateWidthToPercent(width, x);
          const time = videoDuration * percent;
          const minOutTime: number = Number(videoInTime) + Number(videoPartialMinTime);

          if (time >= minOutTime) {
            setScrubbingPointX(x);
            setCurrentPointX(x);
            setOutPointX(x);
            setScrubbingPercent(percent * 100);
            setVideoOutTime(time);
            setVideoCurrentTime(time);
            videoPlayerRef.current.currentTime = time;
          }
        }
      };
      window.addEventListener('mousemove', mouseMoveOut, false);

      return () => {
        window.removeEventListener('mousemove', mouseMoveOut);
      };
    }, [draggableOutPoint, videoDuration, outPointX, videoInTime, videoPartialMinTime]);

    useLayoutEffect(() => {
      function onResize() {
        if (videoPlayerRef.current) {
          const { offsetWidth } = videoPlayerRef.current;
          const inPercent = videoInTime / videoDuration;
          const outPercent = videoOutTime / videoDuration;
          setInPointX(offsetWidth * inPercent);
          setOutPointX(offsetWidth * outPercent);
        }
      }

      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('resize', onResize);
      };
    }, [videoInTime, videoOutTime, videoDuration]);

    useEffect(() => {
      const integerPercent: number = parseInt(scrubbingPercent.toFixed(0));
      integerPercent % 2 === 0 && setSkimPercentY(98 - integerPercent);
    }, [scrubbingPercent]);

    // sound control
    useEffect(() => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.volume = soundValue / 100;
      }
    }, [videoPlayerRef && videoPlayerRef.current, soundValue]);

    // playback rate control
    useEffect(() => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.playbackRate = playbackRate;
      }
    }, [videoPlayerRef && videoPlayerRef.current, playbackRate, isClean]);

    useEffect(() => {
      if (!selectedScript) return;
      setIsEditScript(true);
    }, [onChangeVideoInTime, onChangeVideoOutTime]);

    // 다른 스크립트 선택시 초기화
    useEffect(() => {
      if (isEditScript) {
        setIsEditScript(false);
      }
    }, [selectedScript]);

    // 진입시 자동재생
    useEffect(() => {
      if (!isLoading && isPlay) {
        videoPlayerRef.current.play().catch(e => {
          // 새로고침시에는 보안상 자동재생이 안돼서 수동으로 play 상태 변경
          setIsPlay(false);
          console.log('자동재생 실패', e);
        });
      }
    }, [isLoading]);

    // isClean이 변경되도 현재 시간 유지
    useEffect(() => {
      if (videoPlayerRef.current) {
        setVideoCurrentTime(prevCurrentTime);
        videoPlayerRef.current.currentTime = prevCurrentTime;
      }
    }, [isClean]);

    const onLoadStart = useCallback(() => {
      console.debug('onLoadStart()');
      setIsLoading(true);
    }, []);

    const onLoadedMetadata = useCallback(() => {
      console.debug('onLoadedMetadata()');
      if (videoPlayerRef.current) {
        const { duration, offsetWidth } = videoPlayerRef.current;
        onChangeVideoDuration(duration);

        console.debug({ initInTime, initOutTime });

        if (initInTime) {
          const inTimePercent = initInTime / duration;
          setVideoInTime(initInTime);
          onChangeVideoInTime(initInTime);
          setVideoCurrentTime(initInTime);
          // videoPlayerRef.current.currentTime = initInTime;
          setInPointX(offsetWidth * inTimePercent);
        }

        if (initOutTime) {
          const outTimePercent = initOutTime / duration;
          setVideoOutTime(initOutTime);
          setOutPointX(offsetWidth * outTimePercent);
          onChangeVideoOutTime(initOutTime);
        } else {
          // isClean 변경 => src가 변경된 경우에는 outTime수정 skip
          if (skipOutTimeUpdateRef.current) {
            skipOutTimeUpdateRef.current = false;
            return;
          }
          onChangeVideoOutTime(duration);
          setVideoOutTime(duration);
          setOutPointX(offsetWidth);
        }
        // setIsLoading(false);
      }
    }, [onChangeVideoOutTime, onChangeVideoInTime, onChangeVideoDuration, initInTime, initOutTime]);

    const onLoadedData = useCallback(() => {
      if (videoPlayerRef.current) {
        if (initInTime && videoPlayerRef.current.readyState >= 3) {
          videoPlayerRef.current.currentTime = initInTime;
        }
      }
      console.debug('onLoadedData()');
      setIsLoading(false);
    }, [initInTime]);

    const onLoadingVideo = useCallback(() => {
      setIsLoading(true);
    }, []);

    const onOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setMenuButton(event.currentTarget);
    }, []);

    const onCloseMenu = useCallback(() => {
      setMenuButton(null);
    }, []);

    const onCanPlayVideo = useCallback(
      event => {
        setIsLoading(false);
        setVideoError({ ...videoError, fatal: false });
      },
      [videoError],
    );

    const onSeeked = useCallback(() => {
      if (videoPlayerRef.current) {
        const { readyState } = videoPlayerRef.current;
        if (readyState >= 2) {
          setIsLoading(false);
          setVideoError({ ...videoError, fatal: false });
        }
      }
    }, []);

    const onTimeUpdate = useCallback(() => {
      const videoPlayer = videoPlayerRef.current;
      if (videoPlayer) {
        setVideoCurrentTime(videoPlayer.currentTime);
        onChangeScrollHeight(videoPlayer.currentTime);
        if (playMode === VideoPlayMode.SECTION_PLAY && videoPlayer.currentTime >= outTime) {
          videoPlayer.pause();
          videoPlayer.currentTime = inTime;
          setVideoCurrentTime(inTime);
          onChangeVideoInTime(inTime);
          setIsPlay(false);
        }
      }
    }, [onChangeScrollHeight, playMode, outTime, inTime]);

    const onPlay = useCallback(() => {
      if (videoPlayerRef.current) {
        isPlay ? videoPlayerRef.current.pause() : videoPlayerRef.current.play();
        setIsPlay(!isPlay);
        setPlayMode(VideoPlayMode.NORMAL_PLAY);
      }
    }, [isPlay]);

    const onPlaySection = useCallback(() => {
      if (videoPlayerRef.current) {
        setIsPlay(true);
        setPlayMode(VideoPlayMode.SECTION_PLAY);
        videoPlayerRef.current.currentTime = inTime;
        videoPlayerRef.current.play();
      }
    }, [inTime]);

    const onError = useCallback(() => {
      if (videoPlayerRef.current && videoPlayerRef.current.error) {
        const { code, message } = videoPlayerRef.current.error;

        if (!isClean && !lowresHandled) {
          videoPlayerRef.current.src = selectedAsset?.filePath.lowres.nas || '';
          setLowresHandled(true);
          setProxyHandled(false);
        } else if (isClean && !proxyHandled) {
          videoPlayerRef.current.src = selectedAsset?.filePath.proxy.nas || '';
          setProxyHandled(true);
          setLowresHandled(false);
        }

        const cloudfront = isClean ? selectedAsset?.filePath.proxy.cloudfront : selectedAsset?.filePath.lowres.cloudfront;
        const nas = isClean ? selectedAsset?.filePath.proxy.nas : selectedAsset?.filePath.lowres.nas;

        setVideoError(state => ({
          count: state.count + 1,
          type: String(code),
          details: message,
          fatal: true,
          url: selectedAsset
            ? `cloudfront : ${cloudfront}, NAS : ${nas}`
            : 'URL 이 존재하지 않습니다.',
        }));
      }
    }, [selectedAsset, videoError.count, isClean, lowresHandled, proxyHandled]);

    const onMouseDownInProgress = useCallback(
      event => {
        if (videoPlayerRef.current) {
          const width = event.currentTarget.offsetWidth;
          const x = event.clientX - videoPlayerRef.current.getBoundingClientRect().left;
          const currentTime = (x / width) * videoDuration;
          setCurrentPointX(x);
          setVideoCurrentTime(currentTime);
          setDraggableProgress(true);
          if (isPlay) {
            setIsPlay(false);
            videoPlayerRef.current.pause();
          }

          videoPlayerRef.current.currentTime = currentTime;
        }
      },
      [videoDuration, isPlay],
    );

    const onMouseMoveInProgress = useCallback(
      event => {
        if (!draggableProgress && videoPlayerRef.current) {
          const width = event.currentTarget.offsetWidth;
          const x = event.clientX - videoPlayerRef.current.getBoundingClientRect().left;
          const percent = calculateWidthToPercent(width, x) * 100;
          setScrubbingPercent(percent);
          setScrubbingPointX(x);
        }
      },
      [draggableProgress],
    );

    const onMouseEnterInProgress = useCallback(event => {
      setIsSkimShow(true);
    }, []);

    const onMouseLeaveInProgress = useCallback(
      event => {
        if (!draggableProgress) {
          setIsSkimShow(false);
        }
      },
      [draggableProgress],
    );

    const onMouseDownInPoint = useCallback(event => {
      eventPropagationPrevent(event);

      if (videoPlayerRef.current) {
        videoPlayerRef.current.pause();
        setIsEditScript(true);
        setIsPlay(false);
        setDraggableInPoint(true);
        setIsSkimShow(true);
      }
    }, []);

    const onMouseDownOutPoint = useCallback(event => {
      eventPropagationPrevent(event);

      if (videoPlayerRef.current) {
        videoPlayerRef.current.pause();
        setIsEditScript(true);
        setIsPlay(false);
        setDraggableOutPoint(true);
        setIsSkimShow(true);
      }
    }, []);

    const onClickFullScreen = useCallback(() => {
      const videoPlayer = videoPlayerRef.current;
      videoPlayer && videoPlayer.requestFullscreen();
    }, []);

    const { onThrowError } = useError();

    const onDownloadLowres = useCallback(
      (event: React.MouseEvent) => {
        if (selectedAsset) {
          const gameDate = '20' + selectedAsset.categoryId.toString().slice(0, 6);
          onRequestAssetDownloadCloudfront({
            categoryId: selectedAsset.categoryId,
            fileName: selectedAsset.lowresFileName,
            downloadFileName: `${gameDate}_${selectedAsset.title}.mp4`,
          });
        } else {
          onThrowError(new Error('에셋이 존재하지 않습니다.'));
          return;
        }
      },
      [selectedAsset, onRequestAssetDownloadCloudfront],
    );

    // handle sound
    const onChangeSoundLevel = useCallback(
      (e: any, value: number | number[]) => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.volume = (value as number) / 100;
          setSoundValue(value as number);
        }
      },
      [soundValue],
    );

    // handle playback rate
    const onChangePlaybackRate = useCallback(
      (value: number) => {
        setPlaybackRate(value);
      },
      [playbackRate],
    );

    const onClickPlaybackRate = useCallback(() => {
      setEnableSoundControl(false);
      setVisibleScriptEditController(false);
      setVisiblePlaybackRateControl(!visiblePlaybackRateControl);
    }, [visiblePlaybackRateControl]);

    const onClickScriptEditController = useCallback(() => {
      setEnableSoundControl(false);
      setVisiblePlaybackRateControl(false);
      setVisibleScriptEditController(!visibleScriptEditController);
    }, [visibleScriptEditController]);

    const handleRequestPutScript = () => {
      if (!selectedScript || !isEditScript || !hasAuthEditScript) return;
      const scriptParam: ScriptPutParamVOProps = {
        ID: selectedScript.id,
        IN_POINT: Math.floor(videoInTime * fps),
        OUT_POINT: Math.floor(videoOutTime * fps),
      };
      onRequestPutScript(scriptParam);
      setIsEditScript(false);
      setVisibleScriptEditController(false);
      setShowMessageToast(true);
    };

    useEffect(() => {
      if (!isLoadingPut && showMessageToast) {
        const timer = setTimeout(() => {
          setShowMessageToast(false);
        }, 1200);
        return () => clearTimeout(timer);
      }
    }, [isLoadingPut, showMessageToast]);

    useLayoutEffect(() => {
      function onClose(event: MouseEvent) {
        if (enableSoundControl || visiblePlaybackRateControl || visibleScriptEditController) {
          setEnableSoundControl(false);
          setVisiblePlaybackRateControl(false);
          setVisibleScriptEditController(false);
        }
      }

      document.addEventListener('click', onClose, false);

      return () => {
        document.removeEventListener('click', onClose);
      };
    }, [enableSoundControl, visiblePlaybackRateControl, visibleScriptEditController]);

    /**
     * [모바일]
     */
    useLayoutEffect(() => {
      const touchMoveProgress = (event: TouchEvent) => {
        if (draggableProgress && videoPlayerRef.current) {
          eventPropagationPrevent(event);
          const videoPlayer = videoPlayerRef.current;
          const width = videoPlayer.offsetWidth;
          const x = event.touches[0].clientX - videoPlayer.getBoundingClientRect().left;
          const percent = calculateWidthToPercent(width, x);
          const videoCurrentTime = percent * videoPlayer.duration;

          setCurrentPointX(x);
          setScrubbingPointX(x);
          setScrubbingPercent(percent * 100);
          setVideoCurrentTime(videoCurrentTime);
          videoPlayerRef.current.currentTime = videoCurrentTime;
        }
      };
      window.addEventListener('touchmove', touchMoveProgress, {
        passive: false,
      });

      return () => {
        window.removeEventListener('touchmove', touchMoveProgress);
      };
    }, [draggableProgress]);

    const onTouchStartProgress = useCallback((event: React.TouchEvent) => {
      setDraggableProgress(true);
      setIsSkimShow(true);
    }, []);

    const onTouchEndProgress = useCallback((event: React.TouchEvent) => {
      setDraggableProgress(false);
      setIsSkimShow(false);
    }, []);

    useLayoutEffect(() => {
      const touchMoveInPoint = (event: TouchEvent) => {
        if (draggableInPoint && videoPlayerRef.current) {
          eventPropagationPrevent(event);
          const videoPlayer = videoPlayerRef.current;
          const x = event.touches[0].clientX - videoPlayer.getBoundingClientRect().left;
          const width = videoPlayer.offsetWidth;
          const percent = calculateWidthToPercent(width, x);
          const time = videoDuration * percent;
          const minInTime: number = Number(videoOutTime) - Number(videoPartialMinTime);

          if (time <= minInTime) {
            setScrubbingPointX(x);
            setCurrentPointX(x);
            setInPointX(x);
            setScrubbingPercent(percent * 100);
            setVideoInTime(time);
            setVideoCurrentTime(time);
            videoPlayerRef.current.currentTime = time;
          }
        }
      };
      window.addEventListener('touchmove', touchMoveInPoint, {
        passive: false,
      });

      return () => {
        window.removeEventListener('touchmove', touchMoveInPoint);
      };
    }, [draggableInPoint, videoDuration, videoOutTime, videoPartialMinTime]);

    const onTouchStartInPoint = useCallback(() => {
      setDraggableInPoint(true);
      setIsSkimShow(true);
    }, []);

    const onTouchEndInPoint = useCallback(() => {
      setDraggableInPoint(false);
      setIsSkimShow(false);
      setIsEditScript(true);
      onChangeVideoInTime(videoInTime);
    }, [videoInTime]);

    useLayoutEffect(() => {
      const touchMoveOutPoint = (event: TouchEvent) => {
        if (draggableOutPoint && videoPlayerRef.current) {
          eventPropagationPrevent(event);
          const videoPlayer = videoPlayerRef.current;
          const x = event.touches[0].clientX - videoPlayer.getBoundingClientRect().left;
          const width = videoPlayer.offsetWidth;
          const percent = calculateWidthToPercent(width, x);
          const time = videoDuration * percent;
          const minOutTime: number = Number(videoInTime) + Number(videoPartialMinTime);

          if (time >= minOutTime) {
            setScrubbingPointX(x);
            setCurrentPointX(x);
            setOutPointX(x);
            setScrubbingPercent(percent * 100);
            setVideoOutTime(time);
            setVideoCurrentTime(time);
            videoPlayerRef.current.currentTime = time;
          }
        }
      };
      window.addEventListener('touchmove', touchMoveOutPoint, {
        passive: false,
      });

      return () => {
        window.removeEventListener('touchmove', touchMoveOutPoint);
      };
    }, [draggableOutPoint, videoDuration, videoInTime, videoPartialMinTime]);

    const onTouchStartOutPoint = useCallback(() => {
      setDraggableOutPoint(true);
      setIsSkimShow(true);
    }, []);

    const onTouchEndOutPoint = useCallback(() => {
      setDraggableOutPoint(false);
      setIsSkimShow(false);
      setIsEditScript(true);
      onChangeVideoOutTime(videoOutTime);
    }, [videoOutTime]);

    const handleChangeVideoInTime = (inTime: number) => {
      if (videoPlayerRef.current) {
        const percent = inTime / videoDuration;
        const x = videoPlayerRef.current.offsetWidth * percent;
        setVideoInTime(inTime);
        onChangeVideoInTime(inTime);
        setInPointX(x);
        videoPlayerRef.current.currentTime = inTime;
      }
    };

    const handleChangeVideoOutTime = (outTime: number) => {
      if (videoPlayerRef.current) {
        const percent = outTime / videoDuration;
        const x = videoPlayerRef.current.offsetWidth * percent;
        setVideoOutTime(outTime);
        onChangeVideoOutTime(outTime);
        setOutPointX(x);
        videoPlayerRef.current.currentTime = outTime;
      }
    };

    const handleClickCleanSwitch = () => {
      if (videoPlayerRef.current) {
        skipOutTimeUpdateRef.current = true;

        const prev = videoPlayerRef.current.currentTime;
        setIsClean(!isClean);
        setPrevCurrentTime(prev);
      }
    };

    return (
      <div className={classes.root}>
        <Loading isLoading={isLoading} />
        <HlsError error={videoError} />
        <div className={classes.videoContainer} onClick={onPlay}>
          {/*
            비디오 태그 실행 순서
            [1] onloadstart
            [2] ondurationchange
            [3] onloadedmetadata
            [4] onloadeddata
            [5] onprogress
            [6] oncanplay
            [7] oncanplaythrough
          */}
          {selectedAsset && (
            <video
              // crossOrigin="anonymous"
              ref={videoPlayerRef}
              controls={false}
              controlsList="nodownload"
              preload="auto"
              poster={thumbnailPath}
              src={isClean && proxyPath ? proxyPath : lowresPath}
              playsInline
              onLoadStart={onLoadStart}
              onLoadedMetadata={onLoadedMetadata}
              onLoadedData={onLoadedData}
              onCanPlay={onCanPlayVideo}
              onCanPlayThrough={onCanPlayVideo}
              onTimeUpdate={onTimeUpdate}
              onAbort={onLoadingVideo}
              onWaiting={onLoadingVideo}
              onPlaying={onCanPlayVideo}
              onSeeked={onSeeked}
              onError={onError}
            >
              <track src="" kind="captions" />
            </video>
          )}
          <div className={clsx(classes.coverContainer)}>
            <img
              src={VideoPlayIcon}
              draggable={false}
              className={clsx((isPlay || isLoading) && classes.opacity)}
            />
          </div>
        </div>
        <div className={classes.functionContainer}>
          <Skim
            isShow={isSkimShow}
            backgroundImage={selectedAsset?.getSkimBackgroundURL() || ''}
            timecode={scrubbingTimecode}
            skimPixelX={skimPixelX}
            skimPercentY={skimPercentY}
          />

          <div
            className={classes.progressBarContainer}
            onMouseMove={onMouseMoveInProgress}
            onMouseDown={onMouseDownInProgress}
            onMouseEnter={onMouseEnterInProgress}
            onMouseLeave={onMouseLeaveInProgress}
            onTouchStart={onTouchStartProgress}
            onTouchEnd={onTouchEndProgress}
          >
            <div className={classes.progressBar} style={{ left: currentPointLeftPercent }}></div>
            <div
              className={clsx(classes.scrubbingBar, isSkimShow || classes.displayNone)}
              style={{
                left: scrubbingPointX,
              }}
            />
          </div>
          <div className={classes.inOutContainer}>
            <div
              className={clsx(classes.pointContainer, classes.inPointContainer)}
              style={{ left: inPointLeftPercent }}
            >
              <img
                src={VideoStartPointWebIcon}
                draggable={false}
                className={classes.point}
                onMouseDown={onMouseDownInPoint}
                onTouchStart={onTouchStartInPoint}
                onTouchEnd={onTouchEndInPoint}
              />
            </div>
            <div
              className={clsx(classes.pointContainer, classes.outPointContainer)}
              style={{ right: outPointRightPercent }}
            >
              <img
                src={VideoFinishPointWebIcon}
                draggable={false}
                className={clsx(classes.point)}
                onMouseDown={onMouseDownOutPoint}
                onTouchStart={onTouchStartOutPoint}
                onTouchEnd={onTouchEndOutPoint}
              />
            </div>
          </div>
          <div className={classes.timeContainer}>
            <span>{currentTimecode}</span>
            <span>{durationTimecode}</span>
          </div>

          <div className={classes.controllerContainer}>
            <div className={classes.playButtonArea}>
              <IconButton onClick={onPlay} className={classes.iconButton}>
                {isPlay ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <IconButton className={classes.iconButton} onClick={onPlaySection}>
                {isPlay ? (
                  <SectionPause width="24px" height="24px" color="#ffffff" />
                ) : (
                  <SectionPlay width="24px" height="24px" color="#ffffff" />
                )}
              </IconButton>
              <span style={{ position: 'relative' }}>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  className={clsx(
                    classes.soundControllerContainer,
                    enableSoundControl || classes.soundControllerHidden,
                  )}
                >
                  <Grid item>
                    <Slider
                      className={classes.soundController}
                      orientation="vertical"
                      value={soundValue}
                      aria-labelledby="vertical-slider"
                      onChange={onChangeSoundLevel}
                    />
                  </Grid>
                </Grid>
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {
                    setVisiblePlaybackRateControl(false);
                    setEnableSoundControl(!enableSoundControl);
                  }}
                >
                  <VolumeUpIcon />
                </IconButton>
              </span>
              <span style={{ position: 'relative' }}>
                <PlaybackRateController
                  visible={visiblePlaybackRateControl}
                  current={playbackRate}
                  onChange={onChangePlaybackRate}
                />
                <IconButton className={classes.iconButton} onClick={onClickPlaybackRate}>
                  <Typography variant="caption">{playbackRate} x</Typography>
                </IconButton>
              </span>
            </div>

            {isShow && (
              <label className={classes.cleanSwitchArea}>
                <input
                  type="checkbox"
                  checked={isClean}
                  onChange={handleClickCleanSwitch}
                  className={classes.displayNone}
                />
                <div className={classes.cleanSwitch}>
                  <div
                    style={{
                      color: isClean ? 'grey' : '#ffffff',
                    }}
                    className={classes.cleanSwitchItem}
                  >
                    방송본
                  </div>
                  <div
                    style={{
                      color: isClean ? '#ffffff' : 'grey',
                    }}
                    className={classes.cleanSwitchItem}
                  >
                    클린본
                  </div>
                  <div
                    className={clsx(classes.cleanSwitchItemBackground, isClean && 'isClean')}
                    style={{
                      left: isClean ? '52px' : '4px',
                    }}
                  ></div>
                </div>
              </label>
            )}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {hasAuthEditScript && (
                <span style={{ position: 'relative' }}>
                  {/* 스크립트 편집 컨트롤러 */}
                  {showMessageToast && message && !isLoadingPut && (
                    <div className={classes.messageToast}>
                      <div
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.8)',
                          color: 'white',
                          padding: '8px 12px',
                          fontSize: '12px',
                        }}
                      >
                        {message}
                      </div>
                    </div>
                  )}
                  {isLoadingPut && (
                    <CircularProgress size="0.9em" className={classes.loadingIcon} />
                  )}
                  <IconButton
                    className={classes.iconButton}
                    onClick={handleRequestPutScript}
                    disabled={!isEditScript || !selectedScript}
                  >
                    <SaveIcon
                      className={clsx(
                        isEditScript && selectedScript
                          ? classes.saveIcon
                          : classes.saveIconDisabled,
                      )}
                    />
                  </IconButton>
                </span>
              )}

              <span style={{ position: 'relative' }}>
                <IconButton className={classes.iconButton} onClick={onClickFullScreen}>
                  <FullscreenIcon />
                </IconButton>
              </span>
              <span style={{ position: 'relative' }}>
                <IconButton
                  className={classes.iconButton}
                  onClick={onDownloadLowres}
                  disabled={isDownloading}
                >
                  {isDownloading ? <CircularProgress size="0.9em" /> : <GetAppIcon />}
                </IconButton>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
